import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';

import './SatelliteCourseMap.css';

import { getQueueGraphSnapshot } from '../utils/fetchTransformQueue';
import { buildSatelliteTrackWindow, SatelliteTrackPoint } from '../domain/satelliteTrack';

const WINDOW_SECONDS = 120;
const TILE_SIZE = 256;
const CARTO_SUBDOMAINS = ['a', 'b', 'c', 'd'];
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

interface MapSize {
  width: number;
  height: number;
}

interface WorldPoint {
  x: number;
  y: number;
}

function lonToX(lon: number, worldSize: number): number {
  return ((lon + 180) / 360) * worldSize;
}

function latToY(lat: number, worldSize: number): number {
  const clamped = Math.max(-85, Math.min(85, lat));
  const rad = (clamped * Math.PI) / 180;
  const merc = Math.log(Math.tan(Math.PI / 4 + rad / 2));
  return ((1 - merc / Math.PI) / 2) * worldSize;
}

function toWorldPoint(latitude: number, longitude: number, zoom: number): WorldPoint {
  const worldSize = TILE_SIZE * 2 ** zoom;
  return {
    x: lonToX(longitude, worldSize),
    y: latToY(latitude, worldSize),
  };
}

function normalizeLongitude(lon: number): number {
  let n = lon;
  while (n <= -180) n += 360;
  while (n > 180) n -= 360;
  return n;
}

function circularMeanLongitude(longitudes: number[]): number {
  if (longitudes.length === 0) {
    return 0;
  }
  const sumSin = longitudes.reduce((sum, lon) => sum + Math.sin((lon * Math.PI) / 180), 0);
  const sumCos = longitudes.reduce((sum, lon) => sum + Math.cos((lon * Math.PI) / 180), 0);
  const angle = Math.atan2(sumSin, sumCos) * (180 / Math.PI);
  return normalizeLongitude(angle);
}

function wrappedLongitudeSpan(longitudes: number[]): number {
  if (longitudes.length < 2) {
    return 0;
  }
  const normalized = longitudes.map(normalizeLongitude).sort((a, b) => a - b);
  let largestGap = -Infinity;
  for (let i = 0; i < normalized.length; i++) {
    const current = normalized[i];
    const next = i === normalized.length - 1 ? normalized[0] + 360 : normalized[i + 1];
    const gap = next - current;
    if (gap > largestGap) {
      largestGap = gap;
    }
  }
  return Math.max(0, 360 - largestGap);
}

function chooseZoom(points: SatelliteTrackPoint[]): number {
  if (points.length < 2) {
    return 2;
  }

  const latitudes = points.map((p) => p.latitude);
  const longitudes = points.map((p) => p.longitude);
  const latSpan = Math.max(...latitudes) - Math.min(...latitudes);
  const lonSpan = wrappedLongitudeSpan(longitudes);
  const span = Math.max(latSpan, lonSpan);

  if (span > 120) return 1;
  if (span > 60) return 2;
  if (span > 25) return 3;
  if (span > 12) return 4;
  return 5;
}

function clampZoom(zoom: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
}

function distanceBetweenTouches(touches: React.TouchList): number | null {
  if (touches.length < 2) {
    return null;
  }
  const [a, b] = [touches[0], touches[1]];
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

const SatelliteCourseMap: React.FC = () => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const pinchGestureRef = useRef<{ startDistance: number; startZoom: number } | null>(null);
  const [mapSize, setMapSize] = useState<MapSize>({ width: 900, height: 420 });
  const [points, setPoints] = useState<SatelliteTrackPoint[]>([]);
  const [playbackHeadTimeIso, setPlaybackHeadTimeIso] = useState<string | null>(null);
  const [zoomOverride, setZoomOverride] = useState<number | null>(null);

  useEffect(() => {
    const element = mapRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const updateSize = () => {
      setMapSize({
        width: Math.max(1, element.clientWidth),
        height: Math.max(1, element.clientHeight),
      });
    };
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const refresh = () => {
      const snapshot = getQueueGraphSnapshot();
      const trackWindow = buildSatelliteTrackWindow(
        snapshot.processedDataQueue,
        snapshot.playbackHeadTimeIso,
        WINDOW_SECONDS
      );
      setPoints(trackWindow.points);
      setPlaybackHeadTimeIso(snapshot.playbackHeadTimeIso);
    };

    refresh();
    const interval = window.setInterval(refresh, 250);
    return () => window.clearInterval(interval);
  }, []);

  const autoZoom = useMemo(() => chooseZoom(points), [points]);
  const activeZoom = zoomOverride ?? autoZoom;

  const viewport = useMemo(() => {
    const center = points.length > 0
      ? {
          latitude: points.reduce((sum, p) => sum + p.latitude, 0) / points.length,
          longitude: circularMeanLongitude(points.map((p) => p.longitude)),
        }
      : { latitude: 0, longitude: 0 };
    const zoom = activeZoom;
    const worldSize = TILE_SIZE * 2 ** zoom;
    const centerWorld = toWorldPoint(center.latitude, center.longitude, zoom);
    return { center, zoom, worldSize, centerWorld };
  }, [activeZoom, points]);

  const renderedTiles = useMemo(() => {
    const { width, height } = mapSize;
    const { zoom, worldSize, centerWorld } = viewport;
    const minX = centerWorld.x - width / 2;
    const maxX = centerWorld.x + width / 2;
    const minY = centerWorld.y - height / 2;
    const maxY = centerWorld.y + height / 2;

    const firstTileX = Math.floor(minX / TILE_SIZE);
    const lastTileX = Math.floor(maxX / TILE_SIZE);
    const firstTileY = Math.floor(minY / TILE_SIZE);
    const lastTileY = Math.floor(maxY / TILE_SIZE);

    const tileCount = 2 ** zoom;
    const tiles: Array<{ key: string; url: string; left: number; top: number }> = [];
    for (let tx = firstTileX; tx <= lastTileX; tx++) {
      const wrappedX = ((tx % tileCount) + tileCount) % tileCount;
      for (let ty = firstTileY; ty <= lastTileY; ty++) {
        if (ty < 0 || ty >= tileCount) continue;
        const left = tx * TILE_SIZE - minX;
        const top = ty * TILE_SIZE - minY;
        tiles.push({
          key: `${tx}:${ty}`,
          url: `https://${CARTO_SUBDOMAINS[Math.abs((wrappedX + ty) % CARTO_SUBDOMAINS.length)]}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/${zoom}/${wrappedX}/${ty}.png`,
          left,
          top,
        });
      }
    }
    return { tiles, minX, minY, worldSize };
  }, [mapSize, viewport]);

  const polylinePoints = useMemo(() => {
    const { worldSize } = viewport;
    const { minX, minY } = renderedTiles;
    return points.map((point) => {
      const world = toWorldPoint(point.latitude, point.longitude, viewport.zoom);
      let x = world.x;
      // Keep route close to viewport around antimeridian wrap.
      const dx = x - (minX + mapSize.width / 2);
      if (Math.abs(dx) > worldSize / 2) {
        x += dx > 0 ? -worldSize : worldSize;
      }
      return {
        x: x - minX,
        y: world.y - minY,
      };
    });
  }, [points, renderedTiles, viewport, mapSize.width]);

  const path = polylinePoints.map((p) => `${p.x},${p.y}`).join(' ');
  const hasTrack = polylinePoints.length > 1;

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const distance = distanceBetweenTouches(event.touches);
    if (!distance) {
      return;
    }
    pinchGestureRef.current = {
      startDistance: distance,
      startZoom: activeZoom,
    };
    event.preventDefault();
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    const gesture = pinchGestureRef.current;
    if (!gesture) {
      return;
    }
    const distance = distanceBetweenTouches(event.touches);
    if (!distance || gesture.startDistance <= 0) {
      return;
    }

    const zoomDelta = Math.log2(distance / gesture.startDistance);
    const nextZoom = clampZoom(Math.round(gesture.startZoom + zoomDelta));
    setZoomOverride((current) => (current === nextZoom ? current : nextZoom));
    event.preventDefault();
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length < 2) {
      pinchGestureRef.current = null;
    }
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!event.ctrlKey) {
      return;
    }
    const zoomStep = event.deltaY < 0 ? 1 : -1;
    setZoomOverride((current) => clampZoom((current ?? autoZoom) + zoomStep));
    event.preventDefault();
  };

  return (
    <div className="scMapShell">
      <div className="scMapTop">
        <h2>Satellite Course Map</h2>
        <div className="scMapMeta">
          <span>
            Window aligned to graph ({WINDOW_SECONDS}s), head:{' '}
            {playbackHeadTimeIso ? dayjs(playbackHeadTimeIso).format('YYYY-MM-DD HH:mm:ss[Z]') : 'Waiting for playback...'}
          </span>
        </div>
      </div>

      <div
        className="scMapCanvas"
        ref={mapRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onWheel={handleWheel}
      >
        {renderedTiles.tiles.map((tile) => (
          <img
            key={tile.key}
            src={tile.url}
            className="scTile"
            style={{ left: `${tile.left}px`, top: `${tile.top}px` }}
            alt=""
            draggable={false}
          />
        ))}
        <svg className="scOverlay" viewBox={`0 0 ${mapSize.width} ${mapSize.height}`} preserveAspectRatio="none">
          {hasTrack && <polyline points={path} className="scTrackLine" />}
          {polylinePoints[0] && <circle cx={polylinePoints[0].x} cy={polylinePoints[0].y} r={5} className="scTrackStart" />}
          {polylinePoints[polylinePoints.length - 1] && (
            <circle
              cx={polylinePoints[polylinePoints.length - 1].x}
              cy={polylinePoints[polylinePoints.length - 1].y}
              r={5}
              className="scTrackHead"
            />
          )}
        </svg>

        {!hasTrack && (
          <div className="scMapEmpty">
            Press Start in Player to load trajectory points for this rolling window.
          </div>
        )}
      </div>
      <div className="scMapAttribution">
        Basemap © OpenStreetMap contributors © CARTO
      </div>
    </div>
  );
};

export default SatelliteCourseMap;
